import type { Message, PipelineStage } from '../../types';
import type { StageKind, StageStatus } from '../displayStrategy';
import type { StageBufferKey } from '../generationStreamState';
import type { StageTimelineItem } from '../stageTimelineAdapter';
import { latestPlanReview, requirementAnalysisConclusion } from '../stageTimelineAdapter';
import { getDisplayCodeLineCount } from '../codeDisplay';
import { serverDeclaredNoProgram } from '../messageTopic';
import {
  getEffectiveValidationReport,
  getRepairBlockedContext,
  getRepairDisplayStatus,
  getRepairTimelineSummary,
  isRepairOutcomeSuccess,
} from '../repairDisplayState';
import { formatValidationRootCauseCount } from '../validationRootCauseSummary';
import { extractTaggedSection } from '../structuredResponse';
import { keepLastAnalysisDraft, stripInternalScaffolding } from '../reasoningDisplay';
import { deliveryValidationLabel, readDeliveryReview } from '../deliveryValidationLabel';

/**
 * One step of the turn: what the model was thinking, and what it decided.
 *
 * A turn is a sequence of these, in the order the stages ran — the same shape a
 * mainstream agent UI shows, where each pass of the loop leaves behind an
 * optional thought and a conclusion written in body type.
 *
 * `conclusion` is absent while the step is still thinking, and stays absent for
 * a finished step that decided nothing worth stating — a conclusion invented by
 * trimming the reasoning prose is worse than none at all.
 *
 * A step is listed as soon as it has anything to show and is never taken back:
 * reasoning the user watched arrive must not vanish later just because the step
 * turned out to have no conclusion. Only steps that started and produced
 * nothing at all are left out.
 */
export interface TurnStep {
  key: TurnStepKey;
  label: string;
  conclusion?: string;
  thought?: string;
  status: StageStatus;
  startedAt?: number;
  /** When the stage stopped — how the turn knows how long it has been between steps. */
  endedAt?: number;
  durationMs?: number;
  /**
   * Said under the step's name when the step is not on its first attempt.
   *
   * A restart clears what the step wrote and restarts its clock, which is right
   * — but done silently it reads as a timer jumping from four minutes back to
   * zero. The server has always sent the sentence; nothing ever showed it.
   */
  notice?: string;
  /**
   * Whether this step's reasoning starts open, overriding the stream's own rule.
   *
   * The stream opens the step it is writing and nothing else, because a finished
   * step has already said what it concluded.
   *
   * Nothing sets this now. The CODESYS steps used to, from when they were drawn
   * below the delivery as the last thing on the page — there, opening them cost
   * nothing. They are now part of the one timeline and sit *above* the program,
   * where five permanently-open paragraphs push the deliverable off the screen;
   * and what those paragraphs say is fixed text, identical on every run, so the
   * second reading of them carries nothing. Their conclusions — the part that
   * differs per run — are never folded either way.
   */
  defaultOpen?: boolean;
}

/**
 * The CODESYS closed-loop validation steps, which continue the turn after the
 * generation stages have finished.
 *
 * Declared here rather than alongside the card because `TurnStep` is what they
 * become, and the dependency has to run this way: presentation is not allowed
 * to import from components. They are namespaced because they are not pipeline
 * stages — nothing streams them, the browser polls for them over REST — and a
 * bare `native` sitting in the same union as `codeGeneration` would suggest the
 * runtime knows about it.
 */
export type CodesysValidationStepId =
  | 'environment'
  | 'artifact'
  | 'native'
  | 'review'
  | 'result';

/**
 * What a step is keyed by.
 *
 * `StageBufferKey` is `keyof MessageStageBuffers` — a runtime-owned type bound
 * to `PipelineStage`. The validation steps are the browser's own, so widening
 * happens here and `@realplc/types` stays as it is.
 *
 * `'tia:native'` (TA8) is the Siemens counterpart of the `codesys:*` steps: a
 * single row rather than a five-step sequence, because TIA has no
 * browser-polled live sequence the way CODESYS does — see
 * `buildTiaNativeCompileStep`.
 */
export type TurnStepKey = StageBufferKey | `codesys:${CodesysValidationStepId}` | 'tia:native';

/**
 * How the step stream says things in the reader's language.
 *
 * The audit timeline has always translated its stage labels; this surface
 * hardcoded Chinese, so in English the two sat side by side naming the same
 * stage of the same turn in different languages — and this is the primary view
 * of a turn, so it was the untranslated half that dominated the page.
 *
 * Absent, everything reads Chinese exactly as before.
 */
export type Translate = (key: string) => string;

// Keys are written as the English sentence, so a catalogue that has lost one
// still reads as English rather than as an identifier. The Chinese argument is
// for callers with no translator at all — every test here, and anything
// building steps outside React.
function say(t: Translate | undefined, key: string, zh: string): string {
  return t ? t(key) : zh;
}

function sayCount(t: Translate | undefined, key: string, zh: string, count: number): string {
  return say(t, key, zh).replace('{count}', String(count));
}

/**
 * The stages that become steps.
 *
 * `thinking` and `planBasis` are deliberately absent: the first never carries
 * content, and the second re-states what `planGeneration` already said.
 *
 * Three of the keys are this surface's own rather than the timeline's: the two
 * disagree about the Chinese for these stages — 编译验证 against 校验 — and
 * sharing a key would have quietly rewritten the Chinese UI to fix an English
 * one.
 */
const STEP_STAGES: Array<{ key: StageBufferKey; label: string; labelKey: string; auditKind: StageKind }> = [
  { key: 'requirementAnalysis', label: '需求分析', labelKey: 'Requirement Analysis', auditKind: 'requirementAnalysis' },
  { key: 'requirementConfirmation', label: '需求确认', labelKey: 'Requirement Confirmation', auditKind: 'requirementConfirmation' },
  { key: 'planGeneration', label: '方案规划', labelKey: 'Step plan generation', auditKind: 'planGeneration' },
  { key: 'planReview', label: '计划审批', labelKey: 'Plan Review', auditKind: 'planReview' },
  { key: 'codeGeneration', label: '生成代码', labelKey: 'Code Generation', auditKind: 'code' },
  { key: 'validation', label: '预检（内置规则）', labelKey: 'Step compile validation', auditKind: 'validation' },
  { key: 'repair', label: '修复问题', labelKey: 'Step repair issues', auditKind: 'repair' },
  // `codesysValidation` is not a step either (C1, 2026-09-22). Its row,
  // 「原生编译（CODESYS）」, said what step ③ of the five CODESYS rows says; the
  // turn now draws those five from the stage's compile record while it runs
  // (`buildLiveCodesysSteps`). 用户：「原生编译（codesys）要去掉 本身和第3步不是
  // 重复了吗」.
];

/**
 * A conclusion is a short block, never a wall of prose.
 *
 * 120 was sized for the one-line verdicts the templates produce — "计划已通过"、
 * "预检未通过：6 个错误". The requirement analysis now closes in the model's own
 * words: a sentence saying what was understood, two numbered points for what was
 * assumed and what is still open, and a handover to the cards. 120 cut that
 * mid-word. Still bounded — the account of the step lives in the folded prose
 * above, and this is only its conclusion.
 */
const MAX_CONCLUSION_CHARS = 420;

const STREAM_STATUS_TO_STAGE_STATUS: Record<string, StageStatus> = {
  streaming: 'running',
  completed: 'passed',
  needs_input: 'needs_input',
  needs_approval: 'needs_approval',
  failed: 'failed',
  skipped: 'skipped',
};

function sentence(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  // Line breaks survive; runs of spaces do not.
  //
  // This collapsed every kind of whitespace, which was right while a
  // conclusion was one templated sentence. The analysis now closes in the
  // model's own words and carries numbered points, and the conclusion is
  // rendered through Markdown — so flattening the newlines turned a list into
  // one run-on line before Markdown ever saw it.
  const text = value
    .replace(/[ \t\u3000]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!text) return undefined;
  return text.length > MAX_CONCLUSION_CHARS ? `${text.slice(0, MAX_CONCLUSION_CHARS)}…` : text;
}

function planReviewConclusion(message: Message, t?: Translate): string | undefined {
  const planReview = latestPlanReview(message);
  if (!planReview) return undefined;
  if (planReview.requiresApproval === false) {
    return say(t, 'Plan needs no manual approval.', '计划无需人工审批。');
  }
  switch (planReview.reviewResult) {
    // A countdown approval is nobody's confirmation, and said so it read as one.
    case 'approved': return planReview.approvalSource === 'auto'
      ? say(t, 'Plan approved automatically.', '计划已自动批准。')
      : say(t, 'Plan approved (confirmed by user).', '计划已通过（用户确认）。');
    case 'modified': return say(t, 'Plan approved after changes.', '计划已修改后通过。');
    case 'rejected': return say(t, 'Plan rejected.', '计划被拒绝。');
    case 'regenerate_subtask': return say(t, 'Re-planning subtasks.', '正在重新规划子任务。');
    // What the card says of it, and what to do: the plan was kept, and runs on 继续执行.
    case 'timed_out': return say(t, 'Approval window closed; the plan is still yours to run: press Continue Execution.', '审批时间已过，计划仍可执行：点「继续执行」。');
    default: return say(t, 'Waiting for plan confirmation.', '等待计划确认。');
  }
}

/**
 * What this stage decided, drawn from what is actually known about it.
 *
 * Model-written prose outranks a derived metric, because the user asked for the
 * step's conclusion and a line count is a measurement, not a judgement. Nothing
 * here may fall back to the stage's own reasoning text: trimming a thought into
 * a conclusion is what made the previous version read like half-sentences.
 */
function deriveConclusion(message: Message, key: StageBufferKey, t?: Translate): string | undefined {
  switch (key) {
    case 'requirementAnalysis':
      return requirementAnalysisConclusion(message, t);
    case 'requirementConfirmation': {
      const confirmed = message.clarifyState?.confirmedAnswers?.length || 0;
      return confirmed > 0
        ? sayCount(t, '{count} items confirmed.', '{count} 项已确认。', confirmed)
        : undefined;
    }
    case 'planGeneration': {
      // Not `planReview.requirementSummary`: that restates the requirement
      // rather than saying what planning decided, and the review card already
      // shows it. Repeating it here would state the same thing twice.
      const subtasks = message.planProcessSummary?.subtaskCount;
      return subtasks
        ? sayCount(t, 'Split into {count} subtasks.', '拆成 {count} 个实施项。', subtasks)
        : undefined;
    }
    case 'planReview':
      return planReviewConclusion(message, t);
    case 'codeGeneration': {
      // A stage that ended on a terminal reason with no program did not
      // conclude "0 lines" — it concluded that the run produced nothing, and
      // that has to be stated. Left blank the row renders as a bare label, and
      // a bare label reads as a generation that went fine.
      //
      // Asked before `parsed.code`, not after. The browser's parse is a guess
      // and the terminal reason is the server's verdict: on 2026-09-20 the
      // guess had read reasoning prose as a program, so this said「生成 25 行
      // 程序。」over a turn the server had ended without one.
      if (serverDeclaredNoProgram(message)) {
        return say(t, 'No usable program produced; validation stopped.', '未产出可用程序，已停止校验。');
      }
      if (message.parsed?.code) {
        return sayCount(
          t,
          'Generated {count} lines of code.',
          '生成 {count} 行程序。',
          getDisplayCodeLineCount(message.parsed.code),
        );
      }
      return undefined;
    }
    case 'validation': {
      // The preflight's own report, not the loop's latest one.
      //
      // 2026-09-15 线上：这一行跑的时候写「5 类根因（共 16 处）」，整轮结束后
      // 变成「6 类根因（共 19 处）」。预检没有重跑——`getEffectiveValidationReport`
      // 优先返回 `repairAppendix.finalValidation`，所以修复循环每改写一次校验
      // 结果，用户已经读过的预检行就被改写一次。那个优先级对最终判定是对的，
      // 对这一步不是：这一步说的是预检当时看到了什么。
      //
      // `preflightReport` 正是为此存在的（见它在 types 里的注释），
      // `buildPreflightFindings` 已经在读它，唯独这一格漏了。没有封存记录的
      // 回合——早于这个字段的历史会话——仍然读实时报告。
      const report = message.preflightReport ?? getEffectiveValidationReport(message);
      if (!report) return undefined;
      // The root-cause summary is written by another module and stays in its own
      // language for now; only the passing sentence is this table's to say.
      return sentence(formatValidationRootCauseCount(report, message.generationMetrics?.reviewRequired, t))
        || say(t, 'Validation passed.', '校验通过。');
    }
    case 'repair': {
      // Not the stage buffer: it used to carry a written summary and could
      // stand in here, but the repair stage now streams the model's own notes
      // on the same channel every other stage uses for reasoning. Trimming
      // those into a conclusion is the half-sentence problem this table exists
      // to avoid.
      // The handover sentence has one home, and it is not here.
      //
      // The runtime writes it into `summary` (so it streams out as it happens)
      // and into `checkerPhaseSummary` (so it survives the compile segment
      // replacing `summary`). Both reach the turn, and until the compile segment
      // publishes a summary of its own the two are the same words — printed once
      // as this step's conclusion and once at the end of the preflight segment
      // that the sentence is about. Seen live 2026-09-15.
      //
      // The segment keeps it: that is where it happened, and it is drawn outside
      // the fold that closes the moment it becomes true.
      const summary = message.repairAppendix?.summary;
      const spokenBySegment = summary && summary === message.repairAppendix?.checkerPhaseSummary;
      // Unconditional: once the segment has spoken, this step stays silent,
      // full stop. Falling through to the generic status sentence below used
      // to undo that silence whenever `getRepairDisplayStatus` had anything
      // terminal to say — including, after TA10b review #3, a terminal
      // status the stage buffer alone supplied — printing a different,
      // generic conclusion into the exact slot the suppression was meant to
      // leave empty.
      if (spokenBySegment) return undefined;
      return sentence(summary)
        || sentence(getRepairTimelineSummary(getRepairDisplayStatus(message), message.generationMetrics?.reviewRequired, t));
    }
    default:
      return undefined;
  }
}

/**
 * Whether this stage owns detail the turn renders beneath it.
 *
 * The plan card, the repair rounds and the test cases are drawn into the step
 * they came from, so a stage that concluded nothing quotable still has to keep
 * its row — otherwise the row disappears and takes the content with it. Kept
 * here beside the other derivations rather than in the component, so TurnStream
 * needs to know nothing about any of them.
 */
function hasStageDetail(message: Message, key: StageBufferKey): boolean {
  if (key === 'planReview') return Boolean(latestPlanReview(message));
  if (key === 'repair') {
    // A blocked repair spends no rounds, and its card is the only place the
    // reason and the outstanding findings are stated. Dropping the row would
    // take them with it.
    return (message.repairAppendix?.attempts?.length || 0) > 0
      || Boolean(getRepairBlockedContext(message));
  }
  if (key === 'validation') return (message.pipeline?.testCases?.length || 0) > 0;
  return false;
}

/**
 * Whether requirement analysis ran and filed a requirement it cannot quote.
 *
 * Every other step earns its row by having something to say. This one can end
 * up with nothing on both channels at once: the opening line is lifted out of
 * its reasoning and rendered above the steps, so a short analysis leaves the
 * step holding no prose, and a requirement that yielded no countable field
 * leaves it no conclusion either. The row then vanished, taking with it the
 * only statement that the stage ran at all and how long it took.
 *
 * A filed requirement is the proof it ran, so the row stays on that alone —
 * bearing its label, status and duration, and saying nothing it cannot support.
 */
function analysisFiledRequirement(message: Message, key: StageBufferKey): boolean {
  return key === 'requirementAnalysis' && Boolean(message.pipeline?.requirement);
}

function thoughtOf(text: string | undefined): string | undefined {
  const prose = (text || '').replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, '').trim();
  // The scaffolding filter belongs on every surface that shows reasoning, and
  // this is the one the reader actually watches. It was hooked into
  // sanitizeReasoningContent — the timeline and the panels — while the step
  // stream reads its prose through here, so the first line on screen was still
  // 「我们只需要输出三步内容。第一步用[START_ECHO]和[END_ECHO]包一句话…」.
  // Position first, then vocabulary. Dropping the superseded drafts takes their
  // deliberation with them whatever words it used, and leaves the line filter a
  // single report to work on.
  return stripInternalScaffolding(keepLastAnalysisDraft(prose)) || undefined;
}

/**
 * The model's reading of the request, if it wrapped one in the echo markers.
 *
 * Looked up across every stage buffer rather than requirement analysis alone:
 * the runtime files a plan-agent delta under whichever stage the pipeline phase
 * says is current, and the opening arrives while the phase is still settling.
 */
function spokenOpening(message: Message): string | undefined {
  return Object.values(message.stageBuffers || {})
    .map(buffer => thoughtOf(buffer?.opening))
    .find(Boolean);
}

function joinProse(...parts: Array<string | undefined>): string | undefined {
  return parts.filter(Boolean).join('\n\n') || undefined;
}

/**
 * Where the opening sentence ends and the structured analysis begins.
 *
 * A section emoji ends it wherever it appears, with or without the line break
 * the prompt asks for. The free-pool models routinely run the entire report onto
 * one line, and requiring the break left the boundary unfound: a third of the
 * analysis stood in for the opening line, short enough to pass the length bound,
 * so nothing downstream caught it either.
 *
 * Markdown leads still need the break — a hyphen or a numeral inside a sentence
 * is not the start of a section.
 */
const ANALYSIS_SECTION_LEAD =
  /[\u{2705}\u{1F50D}\u{2753}\u{1F4A1}\u{26A1}\u{1F4DD}]|\n\s*(?:#{1,6}\s|[-*]\s|\d+\.\s|\*\*)/u;
/** Past this, a run-on paragraph is reasoning, not an opening line. */
const MAX_OPENING_CHARS = 200;

/**
 * Recovering the opening sentence when the model ignored the echo markers.
 *
 * The analysis prompt asks for a plain spoken sentence first and the emoji
 * sections after it, so the opening can be found by position when it cannot be
 * found by marker. Small models — the free pool in particular — routinely drop
 * the markers, and without this the sentence stays buried in grey folded text
 * while the turn opens with nothing.
 *
 * Returns the remainder too, so the caller can show each part exactly once.
 */
export function splitAnalysisOpening(
  text: string | undefined,
): { opening?: string; rest?: string } {
  const prose = thoughtOf(text);
  if (!prose) return {};

  // A lead at the very start is a boundary too: the report began with its first
  // section, and there is no opening sentence in front of it. It used to be
  // filtered out along with "not found", so the opening ran on to the next
  // blank line instead — production 2026-09-20, three labelled lines in 133
  // characters, under the bound below, printed as the turn's opening line where
  // a paragraph joined them into one, with the sections after the blank line
  // left alone in the fold.
  const breaks = [prose.search(ANALYSIS_SECTION_LEAD), prose.search(/\n\s*\n/)]
    .filter(index => index >= 0);
  const cut = breaks.length ? Math.min(...breaks) : prose.length;
  const opening = prose.slice(0, cut).trim();

  if (!opening || opening.length > MAX_OPENING_CHARS) return { rest: prose };
  return { opening, rest: prose.slice(cut).trim() || undefined };
}

/**
 * The line the turn opens with, and the reasoning left once it is lifted out.
 *
 * Both are decided here, by one policy over whichever channel carried the prose,
 * so a sentence can never be in both places or in neither. Deriving them apart
 * is what allowed two candidates for one line: a locally written sentence was
 * shown by position while the analysis stream was still settling, and the
 * model's own opening displaced it seconds later when it arrived on the echo
 * channel — read from the page, the turn wiped what had just been read.
 *
 * The echo channel settles it whenever the model used it at all: prose written
 * after an opening cannot be the line the turn opened with. Echo prose too long
 * to be an opening is folded rather than dropped — that channel is not part of
 * `text`, so anything refused here has nowhere else left to appear.
 */
export function splitTurnOpening(message: Message): { line?: string; fold?: string } {
  const analysis = thoughtOf(message.stageBuffers?.requirementAnalysis?.text);
  const echo = splitAnalysisOpening(spokenOpening(message));

  if (echo.opening || echo.rest) {
    return { line: echo.opening, fold: joinProse(echo.rest, analysis) };
  }

  const recovered = splitAnalysisOpening(analysis);
  return { line: recovered.opening, fold: recovered.rest };
}

/**
 * The reasoning a stage streamed, as opposed to everything it streamed.
 *
 * Code generation is the exception: its buffer carries the entire tagged
 * response — the program included — because the runtime sends every chunk to
 * both the stage buffer and the message body. Folding a program away as though
 * it were the model's thinking is wrong twice over: it is not reasoning, and it
 * is already on screen in the body underneath. Only the THOUGHT section is.
 */
function stageThought(
  message: Message,
  key: StageBufferKey,
  text: string | undefined,
): string | undefined {
  if (key === 'codeGeneration') {
    // The provider's own reasoning first, then only the THOUGHT section of the
    // response — whether or not the response is tagged at all. The body strips
    // that one section and nothing else, so untagged prose left here as well was
    // printed twice: once grey in the fold, once in body type underneath. The
    // free pool skips the markers routinely, which made the duplicate the common
    // case rather than the edge one.
    //
    // The reasoning arrives on its own buffer, so it is safe to show whole: a
    // thinking model streams it for minutes before the first token of the
    // program, and that stretch is the one this fold exists to fill.
    //
    // While it is still writing, the reasoning goes last: the foot of the box is
    // what following the stream shows, and it has to be the words being written
    // rather than a section that stopped changing.
    const codeReasoning = thoughtOf(message.stageBuffers?.codeGeneration?.reasoning);
    const codeThought = thoughtOf(extractTaggedSection(text, 'THOUGHT'));
    return message.stageBuffers?.codeGeneration?.done === false
      ? joinProse(codeThought, codeReasoning)
      : joinProse(codeReasoning, codeThought);
  }

  // The provider's own reasoning belongs to every stage, not only code
  // generation.
  //
  // A thinking model can spend minutes on `reasoning_content` before it writes
  // the first character of `content` — measured 2026-09-07, requirement
  // analysis ran 161s and produced 16384 reasoning tokens and zero output
  // characters. The server streams that on its own channel and it is already
  // sitting in this stage's buffer; reading only `text` here is what left the
  // user watching "思考中" against a blank panel for three minutes, with no way
  // to tell a working model from a hung one.
  const reasoning = thoughtOf(message.stageBuffers?.[key]?.reasoning);

  // While a stage is still writing, its live reasoning goes last.
  //
  // Scrolling to the bottom has to land on the newest words. With the analysis
  // prose after the reasoning, the foot of the box was a block that had stopped
  // changing — 「✅ 已明确 / ❓ 待确认」 — and the sentence being written sat
  // somewhere in the middle, so following the stream showed the reader
  // something static. Once the stage is done nothing is moving and the
  // conclusion reads better last, which is the order it keeps.
  const streaming = message.stageBuffers?.[key]?.done === false;

  // The opening line is rendered above the steps, so whichever channel carried
  // it, what is left after it is this step's reasoning — and only that.
  if (key === 'requirementAnalysis') {
    const fold = splitTurnOpening(message).fold;
    return streaming ? joinProse(fold, reasoning) : joinProse(reasoning, fold);
  }

  return streaming ? joinProse(thoughtOf(text), reasoning) : joinProse(reasoning, thoughtOf(text));
}

/**
 * Every lookup key a step title is read through. Derived from the table, so a
 * test can check each one has a Chinese entry without keeping a second list.
 */
export const STEP_LABEL_KEYS: readonly string[] = STEP_STAGES.map(stage => stage.labelKey);

function fromStageBuffers(message: Message, isLive: boolean, t?: Translate): TurnStep[] {
  const buffers = message.stageBuffers;
  if (!buffers) return [];

  return STEP_STAGES
    .flatMap(({ key, label, labelKey }) => {
      const buffer = buffers[key];
      if (!buffer?.started) return [];

      const reported = STREAM_STATUS_TO_STAGE_STATUS[buffer.status || '']
        || (buffer.done ? 'passed' : 'running');
      // A turn that was stopped or failed leaves its last stage without a
      // terminal chunk. It is not still thinking, and must not keep a spinner
      // running on a turn that ended — but nor did it pass or fail, so it is
      // reported as neither.
      const settled: StageStatus = reported === 'running' && !isLive ? 'pending' : reported;
      // A preflight that failed and was then repaired is history, and its
      // colour has to say so: the row keeps stating what the preflight saw,
      // but a red cross above a repair that succeeded reads as "still failing"
      // (production, 2026-09-15). Only a repair that actually ended well
      // changes it; a running or abandoned repair leaves the failure standing.
      // A review that timed out closes its stage as completed, but the plan still waits on the user's 继续执行.
      const status: StageStatus = key === 'validation' && settled === 'failed' && isRepairOutcomeSuccess(message)
        ? 'repaired'
        : key === 'planReview' && latestPlanReview(message)?.reviewResult === 'timed_out'
          ? 'needs_approval'
          : settled;

      // A plan review the user has answered is the one stage whose conclusion
      // does not have to wait: their answer is the whole fact and it is
      // complete the moment they give it, so there is nothing half-written to
      // read. Withholding it is what left the amber "waiting for your approval"
      // card unchanged after the plan had been approved.
      const answered = key === 'planReview' && Boolean(latestPlanReview(message)?.reviewResult);

      // Nothing else is derived from a step that is still going: the facts it
      // would be read from are half-written. Until it finishes, the only
      // conclusion it can have is one the model streamed on the conclusion
      // channel — which by construction arrives after its reasoning is done.
      // A sealed conclusion is normally the record of what the step decided.
      // Not here: a row sealed as「生成 N 行程序。」on a stage the server ended
      // without a program was sealed from a wrong parse, and would stay wrong
      // on every reload unless the verdict is allowed to outrank it.
      const stored = key === 'codeGeneration' && serverDeclaredNoProgram(message)
        ? ''
        : sentence(buffer.conclusion);
      const conclusion = stored
        || (buffer.done || answered ? deriveConclusion(message, key, t) : undefined);
      const thought = stageThought(message, key, buffer.text);

      // Anything to show keeps the step: its conclusion, the reasoning the user
      // already watched, the detail drawn underneath it, or the fact that it is
      // under way or waiting on them. Only a stage that started and produced
      // nothing at all is left out.
      const hasSomethingToShow = Boolean(conclusion)
        || Boolean(thought)
        || hasStageDetail(message, key)
        || analysisFiledRequirement(message, key)
        || status === 'running'
        || status === 'needs_input'
        || status === 'needs_approval';
      if (!hasSomethingToShow) return [];

      // A round that ran with the engineer's confirmed answers analysed the
      // requirement again, with those answers. Its row used to read exactly like
      // the first analysis — same name, near-identical text — and looked like
      // the turn had stalled and replayed itself (production, 2026-09-15).
      const analysedWithAnswers = key === 'requirementAnalysis'
        && (message.clarifyState?.confirmedAnswers?.length || 0) > 0;
      const rowLabel = analysedWithAnswers
        ? say(t, 'Requirement Analysis (updated with confirmations)', '需求分析（已按确认更新）')
        : say(t, labelKey, label);

      // The server's own sentence, plus which retry this is. It does not claim
      // the model changed: on a manually chosen model the retry only demotes
      // the previous one, and may well ask it again.
      //
      // Only while the step is under way. The sentence says a regeneration is
      // in progress; under a step that has finished it promises one that is
      // never coming.
      const restarts = buffer.restartCount || 0;
      const notice = restarts > 0 && buffer.message && status === 'running'
        ? `${buffer.message}${sayCount(t, ' (retry {count})', '（第 {count} 次重试）', restarts)}`
        : undefined;

      return [{
        key,
        label: rowLabel,
        conclusion,
        thought,
        status,
        startedAt: buffer.startedAt,
        endedAt: buffer.endedAt,
        durationMs: buffer.durationMs,
        ...(notice ? { notice } : {}),
      }];
    })
    .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
}

/**
 * Reloaded sessions can arrive without stage buffers — they are only guaranteed
 * on the streaming and same-browser paths — so the audit timeline stands in.
 * Its summaries are one-liners rather than full conclusions, which is worse than
 * the live rendering but far better than a turn whose steps all vanished.
 *
 * Requirement analysis keeps its row on the same terms as it does live, so a
 * refresh cannot be what makes the row disappear.
 */
function fromAuditItems(
  message: Message,
  auditItems: StageTimelineItem[],
  t?: Translate,
): TurnStep[] {
  return STEP_STAGES.flatMap(({ key, label, labelKey, auditKind }) => {
    const item = auditItems.find(candidate => candidate.kind === auditKind);
    if (!item) return [];
    const conclusion = sentence(item.summary);
    if (!conclusion && item.status !== 'running' && !analysisFiledRequirement(message, key)) {
      return [];
    }

    return [{
      key,
      label: say(t, labelKey, label),
      conclusion,
      thought: thoughtOf(item.detail),
      status: item.status,
    }];
  });
}

/**
 * @param isLive whether the turn is still running. A turn that was stopped or
 *   failed leaves stages without a terminal chunk; they must not go on looking
 *   like they are still thinking.
 * @param t how the reader's language says a label or a derived conclusion.
 *   Absent, every step reads Chinese exactly as it did before.
 */
export function buildTurnSteps(
  message: Message,
  auditItems: StageTimelineItem[],
  isLive = true,
  t?: Translate,
): TurnStep[] {
  const fromBuffers = fromStageBuffers(message, isLive, t);
  const steps = fromBuffers.length > 0 ? fromBuffers : fromAuditItems(message, auditItems, t);
  const settledSteps = closeOvertakenSteps(dropConfirmationRowShownAsAPanel(message, steps));
  if (!readDeliveryReview(message.generationMetrics?.reviewRequired)?.required) return settledSteps;
  const reviewLabel = deliveryValidationLabel('', message.generationMetrics?.reviewRequired, t);
  // Stored passing conclusions precede the final delivery mark. Keep findings and the compiler's verdict,
  // but never present a marked delivery's zero-finding validation or successful repair as fully passed.
  return settledSteps.map(step => (
    ((step.key === 'validation' || step.key === 'repair') && step.status === 'passed'
      && (/^(?:校验通过|修复后校验通过|Validation passed|Checks passed)[。.!]?$/i.test(step.conclusion ?? '')
        || step.conclusion?.replace(/[。.!]$/, '') === reviewLabel))
      ? { ...step, conclusion: reviewLabel, status: 'warning' as const }
      : step
  ));
}

/**
 * The confirmation step has no row of its own while its panel is on screen.
 *
 * The panel underneath carries the title, the instruction and every card. The
 * row above it carried the same word in an amber box with nothing inside —
 * blocking styling, a cursor that says it does not open, and no conclusion or
 * prose to open onto. Two headings for one thing, and the amber one said less.
 *
 * Only while the panel is there. A finished turn keeps the row, because then it
 * is the only remaining trace that the step happened.
 */
function dropConfirmationRowShownAsAPanel(message: Message, steps: TurnStep[]): TurnStep[] {
  const panelIsRendered = (message.clarificationChecklist?.length ?? 0) > 0;
  if (!panelIsRendered) return steps;
  return steps.filter(step => !(step.key === 'requirementConfirmation' && !step.conclusion && !step.thought));
}

/**
 * A step the pipeline moved past without it finishing is over, whatever its last
 * chunk said.
 *
 * The turn is a sequence, and only one step works at a time. But a stage that
 * fails upstream can leave its buffer open while the pipeline carries on:
 * measured 2026-09-03, the orchestrator exhausted both its candidates, the plan
 * stage never ran at all, and the turn showed
 * `需求分析 · 1m14s · 思考中` next to `生成代码 · 57s · 思考中` — two spinners,
 * one of them for a step that had already been abandoned, and no way for the
 * reader to tell that the first one was never coming back.
 *
 * `isLive` already closes this for a turn that has stopped. This is the other
 * half: while the turn is still running, a step is finished the moment a later
 * one starts. Marked `failed` rather than `skipped` because it did begin — the
 * elapsed time on the row is real — and rather than `passed` because it produced
 * nothing the pipeline went on to use.
 */
/**
 * The conclusion of every stage that has finished, written into the stage's
 * own buffer the moment it finishes.
 *
 * `fromStageBuffers` reads a finished stage's conclusion from `buffer.conclusion`
 * first and derives it from the message's live fields only when there is none.
 * Derivation is a function of the *current* message, so a step the engineer
 * read ten minutes ago changed every time any of those fields changed: on
 * 2026-09-15 the 需求分析 row said «未定义具体 I/O 点位» while it ran and listed
 * concrete I/O after the turn ended, because a later stage rewrote the field
 * the row was derived from. Sealing the derivation at `stage_done` makes a
 * finished step a record of what it concluded rather than a live view.
 *
 * Only a non-empty derivation is sealed: a stage whose facts have not arrived
 * yet keeps reading them live, exactly as before. Returns `undefined` when
 * there is nothing to seal, so callers can skip the write.
 *
 * Requirement analysis is sealed by the `stage_done` after its own, or by the
 * end of the turn. Its sentence points at the confirmation cards when there are
 * some, and the runtime finishes 需求分析 before it sends them: sealed at its own
 * end, a first turn's analysis said nothing about the cards underneath it
 * (2026-09-23). Requirement confirmation ends after the checklist has landed.
 *
 * @param finished the stage whose `stage_done` this seal answers; omitted at
 *   the end of the turn, which seals every finished stage.
 */
export function sealFinishedStageConclusions(
  message: Message,
  t?: Translate,
  finished?: PipelineStage,
): NonNullable<Message['stageBuffers']> | undefined {
  const buffers = message.stageBuffers;
  if (!buffers) return undefined;

  let sealed: NonNullable<Message['stageBuffers']> | undefined;
  for (const { key } of STEP_STAGES) {
    const buffer = buffers[key];
    if (!buffer?.started || !buffer.done || sentence(buffer.conclusion)) continue;
    if (key === 'requirementAnalysis' && finished === 'requirement_analysis') continue;
    const conclusion = deriveConclusion(message, key, t);
    if (!conclusion) continue;
    sealed = { ...(sealed || buffers), [key]: { ...buffer, conclusion } };
  }
  return sealed;
}

export function closeOvertakenStepsForTest(steps: TurnStep[]): TurnStep[] {
  return closeOvertakenSteps(steps);
}

function closeOvertakenSteps(steps: TurnStep[]): TurnStep[] {
  const lastStarted = steps.reduce(
    (latest, step, index) => (step.status === 'pending' ? latest : index),
    -1,
  );
  if (lastStarted <= 0) return steps;
  return steps.map((step, index) => (
    index < lastStarted && step.status === 'running'
      ? { ...step, status: 'failed' as const }
      : step
  ));
}
